/**
 * Labels for the Contests card in pool Settings (ISSUE-1042 R1): a status word
 * and a date range per contest. Pure: no React, no network.
 */
import type { useContestList } from "@sportsmet/contest-sdk/private";

export const CONTESTS_CARD_NEW_LABEL = "New contest";
export const CONTESTS_CARD_EMPTY_MESSAGE = "No contests this season yet.";
export const CONTESTS_CARD_LOADING_LABEL = "Loading contests";
export const CONTESTS_CARD_FAILED_MESSAGE =
  "We could not load this season's contests.";
export const CONTESTS_CARD_RETRY_LABEL = "Try again";
export const CONTESTS_CARD_STATUS_UPCOMING = "Upcoming";
export const CONTESTS_CARD_STATUS_ACTIVE = "Running";
export const CONTESTS_CARD_STATUS_PENDING = "Waiting to be confirmed";
export const CONTESTS_CARD_STATUS_CONFIRMED = "Finished";
export const CONTESTS_CARD_STATUS_CALLED_OFF = "Called off";
// ISSUE-1330. Deleting a contest that has not started. The server decides
// which rows offer it (canDelete on /contest/list).
export const CONTESTS_CARD_DELETE_LABEL = "Delete";
export const CONTESTS_CARD_DELETING_LABEL = "Deleting";
export const CONTESTS_CARD_DELETE_CONFIRM_LABEL = "Delete contest";
export const CONTESTS_CARD_DELETE_CANCEL_LABEL = "Keep it";
export const CONTESTS_CARD_DELETE_BODY =
  "It hasn't started, so no picks are lost. Any entries members already made go with it. You can't undo this.";

export function contestDeleteConfirmTitle(contestName: string): string {
  return `Delete ${contestName}?`;
}

export function contestDeleteButtonLabel(contestName: string): string {
  return `Delete ${contestName}`;
}

export type typeContestsCardStatus = NonNullable<
  NonNullable<ReturnType<typeof useContestList>["data"]>["data"]
>["items"][number]["status"];

export function contestStatusLabel(status: typeContestsCardStatus): string {
  if (status === "upcoming") return CONTESTS_CARD_STATUS_UPCOMING;
  if (status === "active") return CONTESTS_CARD_STATUS_ACTIVE;
  if (status === "pendingOutcome") return CONTESTS_CARD_STATUS_PENDING;
  if (status === "confirmed") return CONTESTS_CARD_STATUS_CONFIRMED;
  // The only remaining member is the called-off status.
  return CONTESTS_CARD_STATUS_CALLED_OFF;
}

const DAY_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

function formatDay(day: string): string | null {
  const parsed = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return DAY_FORMAT.format(parsed);
}

export function contestDateRangeLabel(input: {
  startDate: string;
  endDate: string;
  endDateMode?: "date" | "tbd";
  status?: typeContestsCardStatus;
  effectiveEndDate?: string | null;
}): string {
  const start = formatDay(input.startDate);
  // ISSUE-1095. A TBD contest's stored endDate is the season's last game,
  // but confirmation can end it sooner. Finished rows carry the effective day.
  if (input.endDateMode === "tbd") {
    if (input.status !== "confirmed")
      return start === null ? "" : `${start} to TBD`;
    if (!input.effectiveEndDate)
      return start === null ? "" : `${start} · End date needs review`;
  }
  const end = formatDay(input.effectiveEndDate ?? input.endDate);
  if (start === null || end === null) return "";
  return `${start} to ${end}`;
}

/**
 * One option in the pool screen's Contest selector (#102): the name, the same
 * date range and status words the Contests card shows, never raw API values.
 */
export function contestSelectorLabel(
  item: Parameters<typeof contestDateRangeLabel>[0] & {
    name: string;
    status: typeContestsCardStatus;
  },
): string {
  return [
    item.name,
    contestDateRangeLabel(item),
    contestStatusLabel(item.status),
  ]
    .filter((part) => part !== "")
    .join(" · ");
}
